import { api } from "@/lib/api";

// Universal DOM auto-translation: translates every visible text node & key
// attributes into the selected language via the backend LLM endpoint (cached).
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "SVG", "PATH", "INPUT"]);

let currentLang = "en";
let observer = null;
let applying = false;
let debounceTimer = null;

const trackedText = []; // { node, orig }
const trackedAttr = []; // { el, attr, orig }
const seenText = new WeakSet();
const seenAttr = new WeakSet();

const hasLetter = (s) => /[A-Za-z\u00C0-\u024F\u0370-\u1FFF\u2C00-\uD7FF]/.test(s);

const cacheKey = (lang) => "i18n_cache_" + lang;
function loadCache(lang) { try { return JSON.parse(localStorage.getItem(cacheKey(lang)) || "{}"); } catch { return {}; } }
function saveCache(lang, obj) { try { localStorage.setItem(cacheKey(lang), JSON.stringify(obj)); } catch {} }

function scan() {
  // text nodes
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      if (seenText.has(n)) return NodeFilter.FILTER_REJECT;
      const p = n.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
      if (p.closest("[data-no-translate]")) return NodeFilter.FILTER_REJECT;
      const raw = n.nodeValue;
      if (!raw || !raw.trim() || !hasLetter(raw)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let cur;
  while ((cur = walker.nextNode())) {
    seenText.add(cur);
    trackedText.push({ node: cur, orig: cur.nodeValue, applied: null });
  }
  // attributes
  document.body.querySelectorAll("[placeholder],[title],[aria-label]").forEach((el) => {
    if (el.closest("[data-no-translate]")) return;
    ["placeholder", "title", "aria-label"].forEach((attr) => {
      const v = el.getAttribute(attr);
      if (!v || !v.trim() || !hasLetter(v)) return;
      const k = el;
      if (seenAttr.has(k) && trackedAttr.some((t) => t.el === el && t.attr === attr)) return;
      trackedAttr.push({ el, attr, orig: v, applied: null });
    });
    seenAttr.add(el);
  });
}

function pad(raw, translatedTrimmed) {
  const trimmed = raw.trim();
  const lead = raw.slice(0, raw.indexOf(trimmed));
  const tail = raw.slice(raw.indexOf(trimmed) + trimmed.length);
  return lead + translatedTrimmed + tail;
}

function applyCache(lang) {
  const cache = loadCache(lang);
  applying = true;
  for (let i = trackedText.length - 1; i >= 0; i--) {
    const t = trackedText[i];
    if (!t.node.isConnected) { trackedText.splice(i, 1); continue; }
    const current = t.node.nodeValue;
    // If React changed this node since we last set it, re-capture the new source text
    if (t.applied != null && current !== t.applied) t.orig = current;
    const trimmed = (t.orig || "").trim();
    if (lang === "en") {
      if (current !== t.orig) t.node.nodeValue = t.orig;
      t.applied = t.orig;
      continue;
    }
    const tr = cache[trimmed];
    if (tr) { const val = pad(t.orig, tr); if (current !== val) t.node.nodeValue = val; t.applied = val; }
    else { t.applied = current; }
  }
  for (let i = trackedAttr.length - 1; i >= 0; i--) {
    const t = trackedAttr[i];
    if (!t.el.isConnected) { trackedAttr.splice(i, 1); continue; }
    const current = t.el.getAttribute(t.attr);
    if (t.applied != null && current !== t.applied) t.orig = current || t.orig;
    const trimmed = (t.orig || "").trim();
    if (lang === "en") { t.el.setAttribute(t.attr, t.orig); t.applied = t.orig; continue; }
    const tr = cache[trimmed];
    if (tr) { const val = pad(t.orig, tr); t.el.setAttribute(t.attr, val); t.applied = val; }
    else { t.applied = current; }
  }
  applying = false;
}

async function fetchMissing(lang) {
  const cache = loadCache(lang);
  const needed = new Set();
  trackedText.forEach((t) => { const k = t.orig.trim(); if (t.node.isConnected && !(k in cache)) needed.add(k); });
  trackedAttr.forEach((t) => { const k = t.orig.trim(); if (t.el.isConnected && !(k in cache)) needed.add(k); });
  const list = Array.from(needed);
  if (list.length === 0) return false;
  try {
    const { data } = await api.post("/i18n/translate", { lang, texts: list });
    Object.assign(cache, data.translations || {});
    saveCache(lang, cache);
    return true;
  } catch { return false; }
}

async function run(lang) {
  scan();
  if (lang === "en") { applyCache("en"); return; }
  applyCache(lang);            // instant paint from cache
  const changed = await fetchMissing(lang);
  if (changed) applyCache(lang); // repaint after fetch
}

function setupObserver() {
  if (observer) observer.disconnect();
  observer = new MutationObserver((mutations) => {
    if (applying) return;
    let relevant = false;
    for (const m of mutations) {
      if (m.type === "childList" && (m.addedNodes.length || m.removedNodes.length)) relevant = true;
      if (m.type === "characterData") relevant = true;
      if (m.type === "attributes") relevant = true;
      if (relevant) break;
    }
    if (!relevant) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => run(currentLang), 250);
  });
  observer.observe(document.body, {
    childList: true, subtree: true, characterData: true,
    attributes: true, attributeFilter: ["placeholder", "title", "aria-label"],
  });
}

export function setAutoTranslateLanguage(lang) {
  currentLang = lang || "en";
  setupObserver();
  run(currentLang);
}
